import { computed, ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

// SiteUp V2 — seção FIXADOS da lista de conversas (tela 03, decisão travada em
// 2026-08-08; nunca implementada até 2026-08-10).
//
// O pin é DO USUÁRIO, não da conta: vive em ui_settings, do mesmo jeito que o
// dock de atalhos. Duas pessoas atendendo a mesma caixa fixam clientes
// diferentes — fixar pra todo mundo seria uma decisão de conta, que ninguém
// pediu.
export const PINNED_SETTINGS_KEY = 'pinned_conversations';

// Teto de segurança: a seção fica ACIMA da fila e não é virtualizada. Sem
// limite, alguém fixa 200 conversas e empurra a fila inteira pra fora da tela.
export const MAX_PINNED = 12;

export function usePinnedConversations() {
  const { uiSettings, updateUISettings } = useUISettings();

  const pinnedIds = computed(() => {
    const stored = uiSettings.value?.[PINNED_SETTINGS_KEY];
    if (!Array.isArray(stored)) return [];
    return stored.map(Number).filter(id => Number.isFinite(id));
  });

  const isPinned = id => pinnedIds.value.includes(Number(id));

  // Pins de conversas que sumiram (apagadas, outra conta, fora da listagem)
  // continuavam ocupando o teto — e como não aparecem, não havia como
  // desafixar pela UI. Com 12 fantasmas, FIXADOS virava feature morta.
  // Achado P1 do review da fase 1 (Grok, 2026-08-10).
  const visibleIds = ref([]);

  const canPinMore = computed(() => {
    // O teto conta o que a pessoa CONSEGUE ver e desafixar. Enquanto a lista
    // ainda não resolveu (primeiro render), cai no total — não dá pra
    // liberar teto com base numa lista vazia que só não carregou ainda.
    const relevant = visibleIds.value.length
      ? pinnedIds.value.filter(id => visibleIds.value.includes(id))
      : pinnedIds.value;
    return relevant.length < MAX_PINNED;
  });

  const togglePin = id => {
    const numeric = Number(id);
    if (!Number.isFinite(numeric)) return { ok: false, reason: 'invalid' };

    if (isPinned(numeric)) {
      updateUISettings({
        [PINNED_SETTINGS_KEY]: pinnedIds.value.filter(x => x !== numeric),
      });
      return { ok: true, pinned: false };
    }

    if (!canPinMore.value) return { ok: false, reason: 'limit' };

    // Entra no FIM: a ordem dos fixados é a ordem em que a pessoa fixou, não
    // a da fila. Reordenar por atividade faria o item "fugir" de onde ela
    // deixou — que é justamente o motivo de fixar.
    updateUISettings({
      [PINNED_SETTINGS_KEY]: [...pinnedIds.value, numeric],
    });
    return { ok: true, pinned: true };
  };

  // Separa a lista já filtrada/ordenada da tela em fixados × fila, preservando
  // a ordem de fixagem nos primeiros.
  const splitByPinned = conversations => {
    const list = Array.isArray(conversations) ? conversations : [];
    if (!pinnedIds.value.length) return { pinned: [], queue: list };

    const byId = new Map(list.map(item => [Number(item.id), item]));
    const pinned = pinnedIds.value.map(id => byId.get(id)).filter(Boolean);
    const pinnedSet = new Set(pinned.map(item => Number(item.id)));
    const queue = list.filter(item => !pinnedSet.has(Number(item.id)));
    // Registra o que foi resolvido nesta passada pra o teto contar só o que
    // a pessoa vê. NÃO grava de volta em ui_settings: um filtro/aba que
    // esconde o fixado apagaria o pin de vez — e ele deve voltar quando a
    // pessoa voltar pra aba onde ele aparece.
    visibleIds.value = pinned.map(item => Number(item.id));
    return { pinned, queue };
  };

  // Saída explícita pro caso do teto cheio de fantasmas: some com tudo que
  // não é visível AGORA. É destrutivo de propósito e só roda por ação da
  // pessoa (o alerta de limite oferece isto).
  const clearUnresolvedPins = () => {
    if (!visibleIds.value.length) return { ok: false, reason: 'unknown' };
    const kept = pinnedIds.value.filter(id => visibleIds.value.includes(id));
    const removed = pinnedIds.value.length - kept.length;
    if (!removed) return { ok: false, reason: 'nothing_to_clear' };
    updateUISettings({ [PINNED_SETTINGS_KEY]: kept });
    return { ok: true, removed };
  };

  return {
    pinnedIds,
    isPinned,
    canPinMore,
    togglePin,
    splitByPinned,
    clearUnresolvedPins,
    MAX_PINNED,
  };
}
